import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { SxProps, Theme } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface IDialogueSheet {
    open: boolean;
    onClose: () => void;
    onAgreeClicked: () => void;
    onDisagreeClick: () => void;
    header?: string;
    body?: string;
    positiveText?: string;
    negativeText?: string;
    lighterBg?: boolean;
    negtiveTextStyle?: React.CSSProperties;
    positiveTextStyle?: React.CSSProperties;
    paperStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    negativeButtonSx?: SxProps<Theme>;
    positiveButtonSx?: SxProps<Theme>;
}

export const DialogueSheet: React.FC<IDialogueSheet> = ({
    open,
    onClose,
    onDisagreeClick,
    onAgreeClicked,
    header,
    body,
    lighterBg,
    positiveText,
    negativeText,
    negtiveTextStyle,
    positiveTextStyle,
    paperStyle,
    headerStyle,
    bodyStyle,
    negativeButtonSx,
    positiveButtonSx,
}) => {
    const { themeStyle } = useThemeContext();

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].pure_black,
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    boxShadow: 'none',
                    ...paperStyle,
                },
            }}
            sx={{
                zIndex: 99999,
                '& .MuiBackdrop-root': {
                    ...(lighterBg && { backgroundColor: theme[themeStyle].delete_modal_color }),
                },
                ...(!lighterBg && { backgroundColor: theme[themeStyle].modal_dark }),
            }}
            aria-describedby="alert-dialog-slide-description">
            <DialogTitle
                style={{
                    color: theme[themeStyle].lightWhite,
                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                    ...headerStyle,
                }}>
                {header}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="alert-dialog-slide-description"
                    style={{
                        color: theme[themeStyle].lightWhite,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        whiteSpace: 'pre-line',
                        ...bodyStyle,
                    }}>
                    {body}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onDisagreeClick}
                    sx={negativeButtonSx}
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                        textTransform: 'capitalize',
                        ...negtiveTextStyle,
                    }}>
                    {negativeText ?? 'Cancel'}
                </Button>
                <Button
                    onClick={onAgreeClicked}
                    sx={positiveButtonSx}
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        ...positiveTextStyle,
                    }}>
                    {positiveText ?? 'Proceed'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
